import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, downloadFile, receiptUrl } from "@/lib/api";
import { noAutomaticRateFor } from "@/lib/fxPanel";
import { useSaveKey, withSaveKey } from "@/lib/saveOnce";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useParties } from "@/lib/parties";
import { psLabel, psOwnerName } from "@/lib/payments";
import { Card, Btn, EmptyState, PageHeader, InfoTip } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { money, fmtDate, CURRENCY_FIELD_HELP, localISO } from "@/lib/format";
import { MoneyInput } from "@/lib/accountingFormat";
import { toast } from "sonner";
import { Gift, Plus, Trash, PencilSimple, Info, Warning, DownloadSimple } from "@phosphor-icons/react";
import { ViewButton, RecordView } from "@/components/RecordView";
import { giftFields, RECORD_TITLES } from "@/lib/recordFields";
import { CurrencySelect } from "@/components/CurrencySelect";
import { useFxCoverage, fxCoverageFor } from "@/lib/fxAdvisory";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { useDateErrors } from "@/lib/useDateErrors";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { HomeCurrencyEstimate } from "@/components/HomeCurrencyEstimate";
import { RECEIPT_ACCEPT } from "@/lib/uploads";
import { runUploads, attachmentFailureMessage } from "@/lib/uploadOutcome";
import { AttachmentButton, AttachmentPanel, AttachmentHelp, receiptAttachment, useAttachmentPanel, useReceiptIndex } from "@/components/AttachmentView";
import { curCode, isUSD, sameCurrency } from "@/lib/currencies";
import { DocumentsBox } from "@/components/DocumentsBox";
import { NotesField } from "@/components/NotesField";

export default function Gifts() {
  const qc = useQueryClient();
  const { selectedStudentId } = useAuth();
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: gifts = [] } = useQuery({
    queryKey: ["gifts", selectedStudentId],
    queryFn: async () => (await api.get(`/gifts${selectedStudentId ? `?student_id=${selectedStudentId}` : ""}`)).data,
  });
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  // The gift behind the eye — read only. (ZEN-702)
  const [viewing, setViewing] = useState(null);
  const { data: totalData } = useQuery({
    queryKey: ["gifts-total", selectedStudentId],
    queryFn: async () => (await api.get(`/gifts/total${selectedStudentId ? `?student_id=${selectedStudentId}` : ""}`)).data,
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["gifts"] }); qc.invalidateQueries({ queryKey: ["gifts-total"] }); };
  const nameOf = (id) => students.find((s) => s.id === id)?.full_name || "—";
  const [delTarget, setDelTarget] = useState(null);
  const del = async (id) => { await api.delete(`/gifts/${id}`); toast.success("Deleted"); refresh(); setDelTarget(null); };
  const exportCsv = async () => {
    try {
      await downloadFile("/gifts/export", "gifts.csv",
        selectedStudentId ? { student_id: selectedStudentId } : undefined);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const byCurrency = Object.entries(
    gifts.reduce((m, g) => { const c = curCode(g.currency, "USD"); m[c] = (m[c] || 0) + (g.amount || 0); return m; }, {})
  ).sort((a, b) => (a[0] === "USD" ? -1 : b[0] === "USD" ? 1 : a[0].localeCompare(b[0])));

  return (
    <div>
      {/* The paperclip convention, beside the page name — these are cards at
          every width, so there is no column header to put it in, and this is
          the screen where the owner hit the ambiguity on 2026-08-30. No gap
          line: a gift holds its attachment ids on the record, so this screen
          cannot detect a broken link. (ZEN-455) */}
      <PageHeader title={<>Gifts<span className="ml-1.5 align-middle"><AttachmentHelp testid="gift-paperclip-help" /></span></>}
        subtitle="Expenses not tracked — informational log only. Creates no settlement and is not part of 529 compliance or draw-downs."
        actions={<div className="flex flex-wrap items-center gap-2">
          {gifts.length > 0 && <Btn variant="ghost" onClick={exportCsv} data-testid="gifts-export-btn"><DownloadSimple size={16} weight="bold" /> Export CSV</Btn>}
          <Btn variant="green" onClick={() => { setEditing(null); setDlg(true); }} data-testid="add-gift-btn"><Plus size={16} weight="bold" /> Add gift</Btn>
        </div>} />

      <div className="mb-3 flex items-start gap-2 rounded-lg border border-[#C2C9D1] bg-[#F0F6FF] px-3 py-2.5 text-xs text-[#475569]" data-testid="gifts-info-banner">
        <Info size={16} weight="bold" className="mt-0.5 shrink-0 text-[#034AA2]" />
        <span>Use <b>Contributions (Tracked Expenses)</b> instead if the money is earmarked and should draw down as you tag expenses. Gifts here are purely a record — no reimbursement, no 529 distribution.</span>
      </div>

      {gifts.length === 0 ? (
        <EmptyState icon={Gift} title="No gifts logged yet" subtitle="Record a one-off gift from a relative or friend toward a beneficiary." />
      ) : (
        <>
          <div className="mb-3 space-y-1.5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="gifts-total">
              {/* Per-currency breakdown */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-[#6b7280]">{byCurrency.length > 1 ? "By currency" : "Total"}</span>
                {byCurrency.map(([cur, amt]) => (
                  <span key={cur} className="inline-flex items-center rounded-md bg-[#077A39] px-3 py-1.5 text-sm font-bold text-white" data-testid={`gifts-total-${cur}`}>
                    {money(amt, cur)}
                  </span>
                ))}
              </div>
              {/* Combined USD total — from stored values, no live FX */}
              {totalData && (
                <div className="rounded-md border border-[#034AA2]/30 bg-[#F0F6FF] px-3 py-1.5 text-center" data-testid="gifts-combined-total">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-[#034AA2]">Combined total</div>
                  <div className="text-base font-extrabold text-[#034AA2]">{money(totalData.total_usd, "USD")}</div>
                  <HomeCurrencyEstimate estimate={totalData.home_estimate} />
                </div>
              )}
              {totalData?.manual_rate_count > 0 && (
                <button type="button" onClick={() => { const g = gifts.find(x => x.fx_manual_required || (!x.usd_amount && !isUSD(x.currency))); if (!g) return; setEditing(g); setDlg(true); }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-2.5 py-1.5 text-xs font-medium text-[#92400E] hover:bg-[#FEF3C7] transition-colors"
                  data-testid="gifts-manual-rate-note">
                  <Warning size={14} weight="bold" className="shrink-0" />
                  <span>{totalData.manual_rate_count} gift{totalData.manual_rate_count !== 1 ? "s" : ""} need{totalData.manual_rate_count === 1 ? "s" : ""} a manual rate — edit to fix</span>
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {gifts.map((g) => <GiftCard key={g.id} g={g} studentName={nameOf(g.student_id)} onView={() => setViewing(g)} onEdit={() => { setEditing(g); setDlg(true); }} onDelete={() => setDelTarget(g.id)} />)}
          </div>
        </>
      )}

      <GiftDialog open={dlg} onClose={() => setDlg(false)} editing={editing} students={students} defaultStudent={selectedStudentId} onSaved={refresh} />
      <RecordView
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={RECORD_TITLES.gift}
        subtitle={viewing ? giftLabel(viewing, nameOf(viewing.student_id)) : ""}
        fields={viewing ? giftFields(viewing, { studentName: nameOf(viewing.student_id) }) : []}
        testid="gift-view"
      />
      <DeleteConfirmDialog open={!!delTarget} onClose={() => setDelTarget(null)} onConfirm={() => del(delTarget)}
        title="Delete this gift?" description="This only removes the informational record." />
    </div>
  );
}

// What the attachment is being checked AGAINST, named for anyone who cannot
// see the card it sits inside.
export function giftLabel(g, studentName) {
  const amount = g.currency && !isUSD(g.currency) ? money(g.amount, g.currency) : money(g.amount, "USD");
  const who = `${g.giver_nickname || "Giver"} → ${studentName || "beneficiary"}`;
  return g.date ? `${who}, ${amount} on ${fmtDate(g.date)}` : `${who}, ${amount}`;
}

function GiftCard({ g, studentName, onView, onEdit, onDelete }) {
  // Both lists hold receipt ids; `receipt_ids` is the older field and some
  // gifts still carry one there, so a gift that names the same file in both
  // must not offer it twice.
  const ids = [...new Set([...(g.document_ids || []), ...(g.receipt_ids || [])])];
  const panel = useAttachmentPanel();
  const index = useReceiptIndex(panel.isOpen(g.id));
  const attachments = ids.map((id, i) => receiptAttachment(id, index, `Attachment ${i + 1}`));
  const recordLabel = giftLabel(g, studentName);

  return (
    <Card data-testid={`gift-${g.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 font-bold">
            <Gift size={18} weight="bold" className="text-[#034AA2]" />
            {g.giver_nickname || "Giver"} → {studentName}
          </div>
          <div className="mt-0.5 text-[11px] text-[#6b7280]">{fmtDate(g.date)}</div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {/* The paperclip was decoration: it said a file existed and did
              nothing when clicked. Now it opens the file, underneath this
              card, with the giver, the amount and the date still on screen
              to check it against. (ZEN-351) */}
          <AttachmentButton
            ref={panel.buttonRef(g.id)}
            attachments={attachments}
            recordLabel={recordLabel}
            expanded={panel.isOpen(g.id)}
            panelId={panel.panelId(g.id)}
            onToggle={() => panel.toggle(g.id)}
            testid={`gift-attach-btn-${g.id}`}
          />
          {/* The eye, immediately before the pencil. (ZEN-702) */}
          <ViewButton onClick={onView} testid={`gift-view-${g.id}`} size={15} />
          <button onClick={onEdit} className="rounded p-1.5 text-[#034AA2] hover:bg-[#E9E9EB]" data-testid={`gift-edit-${g.id}`}><PencilSimple size={15} weight="bold" /></button>
          <button onClick={onDelete} className="rounded p-1.5 text-[#F01919] hover:bg-[#E9E9EB]" data-testid={`gift-del-${g.id}`}><Trash size={15} weight="bold" /></button>
        </div>
      </div>
      <div className="mt-2 font-extrabold tnum text-xl">{g.currency && !isUSD(g.currency) ? money(g.amount, g.currency) : money(g.amount, "USD")}</div>
      {g.payment_method && <div className="mt-1 inline-flex items-center rounded-full bg-[#E9E9EB] px-2 py-0.5 text-[11px] font-semibold text-[#475569]" data-testid={`gift-pm-${g.id}`}>via {g.payment_method}</div>}
      {g.note && <div className="mt-2 text-xs text-[#6b7280]">{g.note}</div>}
      {panel.isOpen(g.id) && (
        <AttachmentPanel
          id={panel.panelId(g.id)}
          attachments={attachments}
          recordLabel={recordLabel}
          onClose={() => panel.close(g.id)}
          testid={`gift-attach-panel-${g.id}`}
        />
      )}
    </Card>
  );
}

// Exported so the upload-failure behaviour can be driven directly in a test.
// The rule this dialog now carries — a lost file is never buried under a green
// "Saved" — is worth proving by pressing the button, not by reading the source.
// (ZEN-446)
export function GiftDialog({ open, onClose, editing, students, defaultStudent, onSaved }) {
  const nav = useNavigate();
  const { data: parties } = useParties();
  const { data: accts = [] } = useQuery({ queryKey: ["accounts-cards"], queryFn: async () => (await api.get("/accounts-cards")).data });
  const { onDateValidation, hasDateErrors } = useDateErrors();
  // One key for this opening, sent with every press of Save in it, so a
  // second press records nothing new. (ZEN-759; lib/saveOnce.js)
  const saveKey = useSaveKey(`${open}:${editing?.id || "new"}`);
  const [f, setF] = useState({});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const giverOpts = [{ ref: "owner", nick: parties?.owner?.nickname || "Owner" }, ...(parties?.custom || []).map((p) => ({ ref: `party:${p.id}`, nick: p.nickname }))];
  const [docFiles, setDocFiles] = useState([]);
  // The gift a previous press already created, when that press could not
  // attach every file. See the save function for why this must exist.
  const [createdGift, setCreatedGift] = useState(null);
  const [removeDocId, setRemoveDocId] = useState(null);
  const [removing, setRemoving] = useState(false);
  const receiptIndex = useReceiptIndex(open);
  const emptyForm = () => ({
    student_id: defaultStudent || "", giver_ref: "owner", giver_nickname: "",
    amount: "", currency: "USD", date: localISO(), note: "",
    fx_rate: null, fx_date: null, fx_override: false,
    transfer_legs: [],
  });

  React.useEffect(() => {
    if (open) {
      setF(editing ? { ...editing, transfer_legs: editing.transfer_legs || editing.transfer_hops || [] } : emptyForm());
      // A fresh open is a fresh gift. Carrying the previous one over would aim
      // the next save at a record this form is no longer showing.
      setCreatedGift(null);
      setDocFiles([]);
      setRemoveDocId(null);
    }
  }, [open, editing]); // eslint-disable-line

  const studentNick = students.find((s) => s.id === f.student_id)?.nickname || students.find((s) => s.id === f.student_id)?.full_name?.split(" ")[0] || "Beneficiary";
  // From: owner/party accounts & cards
  const ownerAcctLabels = accts.filter((a) => !a.archived && a.owner_type !== "student").map((a) => psLabel(a, psOwnerName(a, students, parties))).filter(Boolean);
  const fromSuggestions = [...new Set(ownerAcctLabels)];
  // To: student accounts
  const studentAcctLabels = accts.filter((a) => !a.archived && a.owner_type === "student" && a.student_id === f.student_id).map((a) => psLabel(a, psOwnerName(a, students, parties))).filter(Boolean);
  const toSuggestions = [...new Set(studentAcctLabels)];

  // FX conversion
  // Which currencies convert on their own — the server's answer (ZEN-108).
  // Consulted only after a rate lookup has already failed, to decide whether
  // to tick the enter-the-rate-yourself box. Before the answer arrives,
  // `fxCoverageFor` answers "unknown", which ticks the box — the conservative
  // direction: the family can still record the gift (ZEN-388).
  const { coverage: fxCoverage } = useFxCoverage();
  const isNonUSD = !isUSD(curCode(f.currency, "USD"));
  const [fetchingFx, setFetchingFx] = useState(false);
  const [fxFetchFailed, setFxFetchFailed] = useState(false);
  const [fxUsdField, setFxUsdField] = useState("");
  const fxFetchRef = React.useRef(0);
  React.useEffect(() => {
    if (!open || !isNonUSD || f.fx_override) return;
    const giftDate = f.date;
    const giftCurrency = f.currency;
    if (!giftDate || !giftCurrency) return;
    const fetchId = ++fxFetchRef.current;
    setFetchingFx(true);
    setFxFetchFailed(false);
    set("fx_rate", null);
    set("fx_date", null);
    api.post("/fx/preview", { date: giftDate, base: giftCurrency, target: "USD" })
      .then((res) => {
        if (fetchId === fxFetchRef.current) {
          set("fx_rate", res.data.rate);
          set("fx_date", res.data.rate_date || giftDate);
          setFxFetchFailed(false);
        }
      })
      .catch(() => {
        if (fetchId === fxFetchRef.current) {
          setFxFetchFailed(true);
          // Auto-enable manual override for tail currencies
          if (fxCoverageFor(fxCoverage, giftCurrency).state !== "auto") {
            set("fx_override", true);
          }
        }
      })
      .finally(() => { if (fetchId === fxFetchRef.current) setFetchingFx(false); });
  }, [f.currency, f.date, f.fx_override, open]); // eslint-disable-line

  const fxRateRequired = isNonUSD && !fetchingFx && fxFetchFailed && !f.fx_override && !!f.date;

  const fxRate = parseFloat(f.fx_rate) || 0;
  const amt = parseFloat(f.amount) || 0;
  const usdEquiv = isNonUSD && fxRate ? amt * fxRate : amt;
  const giftCur = curCode(f.currency, "USD");

  // Transfer legs logic — balance tracked in gift currency
  const legs = f.transfer_legs || [];
  const giftAmt = parseFloat(f.amount) || 0;
  // Helper: get a leg's contribution in gift currency
  const legGiftEquiv = (l) => {
    const legCur = curCode(l.currency, giftCur);
    if (sameCurrency(legCur, giftCur)) return parseFloat(l.amount) || 0;
    // Cross-currency: use leg override if set, otherwise derive from current gift rate
    if (l.fx_override && l.gift_cur_equivalent) return parseFloat(l.gift_cur_equivalent) || 0;
    const legAmt = parseFloat(l.amount) || 0;
    const rate = parseFloat(l.leg_fx_rate) || fxRate || 0;
    return rate > 0 && legAmt > 0 ? legAmt / rate : 0;
  };
  const giftCurTransferred = legs.reduce((s, l) => s + legGiftEquiv(l), 0);
  const remaining = Math.max(0, giftAmt - giftCurTransferred);

  const addLeg = () => {
    const newLeg = { from: "", to: "", date: "", amount: "", currency: giftCur, is_full: remaining > 0, gift_cur_equivalent: "" };
    if (newLeg.is_full) newLeg.amount = remaining.toFixed(2);
    setF((p) => ({ ...p, transfer_legs: [...(p.transfer_legs || []), newLeg] }));
  };
  const removeLeg = (i) => setF((p) => ({ ...p, transfer_legs: p.transfer_legs.filter((_, idx) => idx !== i) }));
  const setLeg = (i, k, v) => setF((p) => {
    const legs = [...(p.transfer_legs || [])];
    legs[i] = { ...legs[i], [k]: v };
    const legCur = curCode(legs[i].currency, giftCur);
    // When toggling full/partial, auto-fill amount
    // When toggling full/partial OR changing currency on a full leg, recalc amounts
    const shouldRecalcFull = (k === "is_full" && v) || (k === "currency" && legs[i].is_full);
    if (shouldRecalcFull) {
      const otherGiftCurTotal = legs.reduce((s, l, j) => j === i ? s : s + legGiftEquiv(l), 0);
      const remainingGiftCur = Math.max(0, giftAmt - otherGiftCurTotal);
      if (sameCurrency(legCur, giftCur)) {
        legs[i].amount = remainingGiftCur.toFixed(2);
        legs[i].gift_cur_equivalent = "";
      } else {
        const legRate = parseFloat(legs[i].leg_fx_rate) || fxRate || 0;
        legs[i].gift_cur_equivalent = remainingGiftCur.toFixed(2);
        legs[i].amount = legRate > 0 ? (remainingGiftCur * legRate).toFixed(2) : "";
      }
    }
    if (k === "is_full" && !v) {
      legs[i].amount = "";
      legs[i].gift_cur_equivalent = "";
    }
    // When amount changes in a different currency, auto-calc equivalent
    if ((k === "amount" || k === "currency") && !sameCurrency(legCur, giftCur) && !legs[i].is_full) {
      const legAmt = parseFloat(legs[i].amount) || 0;
      const legRate = parseFloat(legs[i].leg_fx_rate) || fxRate || 0;
      // rate = giftCur→legCur (e.g., CAD→USD = 0.73), so giftEquiv = legAmt / rate
      if (legRate > 0 && legAmt > 0) {
        legs[i].gift_cur_equivalent = (legAmt / legRate).toFixed(2);
      }
    }
    // When currency changes back to gift currency, clear equivalent
    if (k === "currency" && sameCurrency(v, giftCur)) {
      legs[i].gift_cur_equivalent = "";
    }
    return { ...p, transfer_legs: legs };
  });

  const save = async () => {
    if (hasDateErrors()) { toast.error("Fix the date errors before saving."); return; }
    if (!f.student_id) { toast.error("Select a beneficiary"); return; }
    if (!f.amount) { toast.error("Enter an amount"); return; }
    const giverNick = f.giver_nickname || giverOpts.find((g) => g.ref === f.giver_ref)?.nick || "";
    // A FILE THAT DID NOT UPLOAD MUST NOT END ON A GREEN "SAVED". (ZEN-446)
    //
    // This loop used to catch each failure, print a red message, carry on, and
    // finish with "Gift saved" — and the green one, being last, is the one a
    // person believes. That is how three bank transfer confirmations came to
    // be missing from a gift that reported success.
    //
    // The files that DID upload are kept: a save is not thrown away over one
    // bad file. What changes is that the failure gets the last word.
    const staged = docFiles;
    const { ok, failed } = await runUploads(
      staged,
      async (df) => {
        const fd = new FormData();
        fd.append("file", df);
        fd.append("student_id", f.student_id);
        fd.append("run_ai", "false");
        fd.append("purpose", "gift");
        try {
          const { data: rec } = await api.post("/receipts/upload", fd);
          return rec.id;
        } catch (e) {
          // A file this household already holds is refused by the upload
          // door. For a gift, point at the kept copy instead. (ZEN-445)
          if (e?.response?.status !== 409) throw e;
          const ask = new FormData();
          ask.append("file", df);
          const kept = await api.post("/gifts/kept-document", ask).then((r) => r.data?.receipt_id, () => null);
          if (!kept) throw e;
          return kept;
        }
      },
      apiErr,
    );
    const docIds = [...new Set([...(f.document_ids || []), ...ok])];
    const body = {
      ...f, amount: parseFloat(f.amount), giver_nickname: giverNick,
      transfer_hops: f.transfer_legs || [],
      // Not sent: the server works the US-dollar figure out from the rate, and a
      // figure rounded here could be $0.00, which it refuses (ZEN-825 review round 2).
      usd_amount: null,
      fx_rate: isNonUSD ? fxRate || null : null,
      fx_date: isNonUSD ? f.fx_date || null : null,
      document_ids: docIds,
    };
    try {
      // The gift being edited, or the one a previous half-failed press already
      // created. Without the second, pressing Save again after a refused file
      // would enter the SAME gift a second time — the money counted twice to
      // recover a document. (The gift edit box cannot yet show or add
      // paperwork of its own, ZEN-445, so trying again from here is the only
      // way back to a documented record.)
      const target = editing || createdGift;
      if (target) await api.put(`/gifts/${target.id}`, body);
      else {
        const { data: made } = await api.post("/gifts", body, withSaveKey(saveKey));
        setCreatedGift(made);
      }
      // The ids that DID attach are written back into the form, so a retry
      // sends them again instead of replacing the whole list with just the
      // late arrival — the saved gift keeps every document it has earned.
      set("document_ids", docIds);
      const miss = attachmentFailureMessage({
        noun: "gift",
        total: staged.length,
        attached: ok.length,
        failed,
        // No retry sentence: Maryse found it confusing (ZEN-724, 24 Oct 2026).
      });
      if (miss) {
        // Only the refused files stay listed, so pressing Save retries exactly
        // those. The box stays OPEN: closing it on a green tick is what hid
        // this in the first place, and there is nowhere else to go from here.
        setDocFiles(failed.map((m) => m.file));
        toast.error(miss.title, { description: miss.description });
        onSaved();
        return;
      }
      setDocFiles([]);
      toast.success(target ? "Updated" : "Gift saved"); onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Both lists hold file ids; `receipt_ids` is the older field (see GiftCard).
  const storedIds = [...new Set([...(f.document_ids || []), ...(f.receipt_ids || [])])];
  const storedDocs = storedIds.map((id, i) => ({
    id, href: receiptUrl(id),
    label: receiptAttachment(id, receiptIndex, `Attachment ${i + 1}`).label,
  }));
  const removeStoredDoc = async () => {
    const id = removeDocId;
    const target = editing || createdGift;
    setRemoving(true);
    try {
      // A saved gift is told now; a gift not saved yet only has its form.
      if (target) await api.delete(`/gifts/${target.id}/documents/${id}`);
      setF((p) => ({
        ...p,
        document_ids: (p.document_ids || []).filter((x) => x !== id),
        receipt_ids: (p.receipt_ids || []).filter((x) => x !== id),
      }));
      if (target) onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    setRemoving(false);
    setRemoveDocId(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Gift</DialogTitle><DialogDescription>Informational record only — no reimbursement or 529 distribution.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <L label="Beneficiary" full><select className="inp" value={f.student_id || ""} onChange={(e) => set("student_id", e.target.value)} data-testid="gift-student"><option value="">Choose a beneficiary…</option>{students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</select></L>

          {/* Giver — dropdown + free text */}
          <L label="Giver">
            <select className="inp" value={f.giver_ref || "owner"} onChange={(e) => { set("giver_ref", e.target.value); if (e.target.value !== "__custom__") set("giver_nickname", ""); }} data-testid="gift-giver">
              {giverOpts.map((g) => <option key={g.ref} value={g.ref}>{g.nick}</option>)}
              <option value="__custom__">Other (type name)…</option>
            </select>
          </L>
          {f.giver_ref === "__custom__" && (
            <L label="Giver name" full>
              <input className="inp" value={f.giver_nickname || ""} onChange={(e) => set("giver_nickname", e.target.value)}
                placeholder="e.g., Grandma Rose, Uncle Joe" data-testid="gift-giver-name" autoFocus />
            </L>
          )}

          <L label="Date"><UnifiedDateInput value={f.date || ""} onChange={(v) => set("date", v)} testid="gift-date" preset="softFutureDate" onValidation={onDateValidation("gift_date")} /></L>
          <L label={<>Currency <InfoTip testid="gift-currency-info" text={CURRENCY_FIELD_HELP} /></>}><CurrencySelect value={curCode(f.currency, "USD")} onChange={(v) => { set("currency", v); if (isUSD(v)) { set("fx_rate", null); set("fx_date", null); set("fx_override", false); } }} testid="gift-currency" /></L>
          <L label="Amount"><MoneyInput currency={curCode(f.currency, "USD")} value={f.amount ?? ""} onChange={(v) => set("amount", v)} testid="gift-amount" /></L>

          {/* FX conversion panel */}
          {isNonUSD && (
            <div className="col-span-2 rounded-md border border-[#C2C9D1] bg-[#E9E9EB]/50 p-2.5" data-testid="gift-fx-panel">
              {/* Manual rate required warning for tail currencies */}
              {fxFetchFailed && f.date && (
                <div className="mb-2 rounded-md border border-[#D97706]/40 bg-[#FFFBEB] px-2.5 py-1.5" data-testid="gift-rate-required">
                  <p className="text-xs font-medium text-[#92400E]">
                    <Warning size={12} weight="bold" className="mr-1 inline" />
                    {noAutomaticRateFor(giftCur)}
                  </p>
                </div>
              )}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                  <Info size={14} className="text-[#034AA2]" /> Currency conversion
                </div>
                <label className="flex shrink-0 items-center gap-1.5 text-xs font-medium">
                  <input type="checkbox" checked={!!f.fx_override}
                    onChange={(e) => {
                      set("fx_override", e.target.checked);
                      if (!e.target.checked) { set("fx_rate", null); set("fx_date", null); setFxUsdField(""); }
                      else {
                        const r = parseFloat(f.fx_rate) || fxRate || 0;
                        const a = parseFloat(f.amount) || 0;
                        setFxUsdField(r > 0 && a > 0 ? (a * r).toFixed(2) : "");
                      }
                    }}
                    data-testid="gift-fx-override" />
                  Manual override
                </label>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm">
                {f.fx_override ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-[#6b7280]">Rate</span>
                      <input type="number" step="0.0001" value={f.fx_rate || ""} onChange={(e) => {
                          set("fx_rate", e.target.value);
                          const r = parseFloat(e.target.value) || 0;
                          const a = parseFloat(f.amount) || 0;
                          setFxUsdField(r > 0 && a > 0 ? (a * r).toFixed(2) : "");
                        }}
                        data-testid="gift-fx-rate-input" placeholder="e.g., 0.7213"
                        className="h-8 w-28 rounded border border-[#C2C9D1] px-2 text-sm" />
                    </label>
                    <span className="text-[#9aa1a9] text-xs font-medium">or</span>
                    <MoneyInput currency="USD" size="sm" className="!w-36" value={fxUsdField}
                      onChange={(v) => {
                        setFxUsdField(v);
                        const u = parseFloat(v) || 0;
                        const a = parseFloat(f.amount) || 0;
                        set("fx_rate", u > 0 && a > 0 ? String((u / a).toFixed(7)) : "");
                      }}
                      testid="gift-fx-usd-direct" placeholder="e.g., 730.07" />
                  </div>
                ) : (
                  <span className="text-[#6b7280]">
                    {fetchingFx ? <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#034AA2] border-t-transparent" />Fetching rate…</span>
                      : fxRate ? <>
                          {`1 ${giftCur} = ${Number(fxRate).toFixed(5)} USD · ${f.fx_source === "manual" ? "Manual" : f.fx_source === "treasury" ? "US Treasury" : "ECB"} · ${f.fx_date || ""}`}
                          {f.fx_date && f.date && f.fx_date !== f.date && (
                            <div className="mt-0.5 text-[10px] text-[#D97706]">Rate from {f.fx_date} — {f.date > localISO() ? "gift date is in the future" : "closest available rate"}</div>
                          )}
                        </>
                        : <span className="italic text-[#F68F51]">Enter a date to auto-fetch rate</span>}
                  </span>
                )}
              </div>
              {fxRate > 0 && amt > 0 && (
                <div className="mt-1.5 text-xs text-[#6b7280]">
                  {f.fx_override
                    ? `≈ ${money(amt, giftCur)} = ${money(usdEquiv, "USD")}`
                    : `≈ ${money(usdEquiv, "USD")}`}
                </div>
              )}
            </div>
          )}

          {/* ── Transfer legs ─────────────────────────────── */}
          <div className="col-span-2 space-y-2">
            <div className="flex items-center justify-between">
              <span className="block text-xs font-bold uppercase text-[#6b7280]">Money trace (optional)</span>
              <button type="button" onClick={addLeg} className="text-xs font-semibold text-[#034AA2] hover:underline" data-testid="gift-add-leg">+ Add leg</button>
            </div>
            {legs.length === 0 && (
              <p className="text-[11px] text-[#9aa1a9]">Track how the gift moves — e.g., Grandpa → Mom → Beneficiary.</p>
            )}
            {legs.map((leg, i) => {
              const legCurrency = curCode(leg.currency, giftCur);
              const isCrossCur = !sameCurrency(legCurrency, giftCur);
              // Remaining in gift currency for this leg's "Full" calculation
              const otherGiftCurTotal = legs.reduce((s, l, j) => j === i ? s : s + legGiftEquiv(l), 0);
              const legRemainingGiftCur = Math.max(0, giftAmt - otherGiftCurTotal);
              return (
                <div key={i} className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-2.5 space-y-2" data-testid={`gift-leg-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">Leg {i + 1}</span>
                    <button type="button" onClick={() => removeLeg(i)} className="text-xs font-bold text-[#9CA3AF] hover:text-red-500" data-testid={`gift-leg-remove-${i}`}>✕</button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="block">
                      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#9aa1a9]">From</span>
                      <input className="inp" list={`gift-leg-from-opts-${i}`} value={leg.from || ""} onChange={(e) => {
                        if (e.target.value === "➕ Add account…") { nav("/accounts"); onClose(); return; }
                        setLeg(i, "from", e.target.value);
                      }} placeholder="Type or select account" data-testid={`gift-leg-from-${i}`} />
                      <datalist id={`gift-leg-from-opts-${i}`}>
                        {fromSuggestions.map((a) => <option key={a} value={a} />)}
                        <option value="➕ Add account…" />
                      </datalist>
                    </div>
                    <div className="block">
                      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#9aa1a9]">To</span>
                      <input className="inp" list={`gift-leg-to-opts-${i}`} value={leg.to || ""} onChange={(e) => {
                        if (e.target.value === "➕ Add account…") { nav("/accounts"); onClose(); return; }
                        setLeg(i, "to", e.target.value);
                      }} placeholder="Type or select account" data-testid={`gift-leg-to-${i}`} />
                      <datalist id={`gift-leg-to-opts-${i}`}>
                        {toSuggestions.map((a) => <option key={a} value={a} />)}
                        <option value="➕ Add account…" />
                      </datalist>
                    </div>
                    <label className="block">
                      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#9aa1a9]">Date</span>
                      <UnifiedDateInput value={leg.date || ""} onChange={(v) => setLeg(i, "date", v)} testid={`gift-leg-date-${i}`} preset="softFutureDate" />
                    </label>
                    <div>
                      <span className="mb-0.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#9aa1a9]">Paid in <InfoTip testid={`gift-leg-currency-info-${i}`} text={CURRENCY_FIELD_HELP} /></span>
                      <CurrencySelect value={legCurrency} onChange={(v) => setLeg(i, "currency", v)} testid={`gift-leg-currency-${i}`} />
                    </div>
                    <div className="col-span-2">
                      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#9aa1a9]">Amount</span>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-xs">
                          <input type="radio" name={`leg-type-${i}`} checked={!!leg.is_full} onChange={() => setLeg(i, "is_full", true)} data-testid={`gift-leg-full-${i}`} />
                          Full
                        </label>
                        <label className="flex items-center gap-1 text-xs">
                          <input type="radio" name={`leg-type-${i}`} checked={!leg.is_full} onChange={() => setLeg(i, "is_full", false)} data-testid={`gift-leg-partial-${i}`} />
                          Partial
                        </label>
                        {leg.is_full ? (
                          <span className="ml-2 text-sm font-semibold tnum">{money(parseFloat(leg.amount) > 0 ? parseFloat(leg.amount) : (isCrossCur && fxRate > 0 ? legRemainingGiftCur * fxRate : legRemainingGiftCur), legCurrency)}</span>
                        ) : (
                          <MoneyInput currency={legCurrency} className="ml-2 !w-40" value={leg.amount ?? ""} onChange={(v) => setLeg(i, "amount", v)}
                            testid={`gift-leg-amount-${i}`} />
                        )}
                      </div>
                    </div>
                    {/* Cross-currency conversion — matches expense card FX panel */}
                    {isCrossCur && (
                      <div className="col-span-2 rounded-md border border-[#C2C9D1] bg-[#E9E9EB]/50 p-2.5" data-testid={`gift-leg-equiv-${i}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-1.5 text-sm font-semibold">
                            <Info size={14} className="text-[#034AA2]" /> Currency conversion
                          </div>
                          <label className="flex shrink-0 items-center gap-1.5 text-xs font-medium">
                            <input type="checkbox" checked={!!leg.fx_override}
                              onChange={(e) => {
                                setLeg(i, "fx_override", e.target.checked);
                                if (!e.target.checked) setLeg(i, "leg_fx_rate", null);
                              }}
                              data-testid={`gift-leg-fx-override-${i}`} />
                            Manual override
                          </label>
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm">
                          {leg.fx_override ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <label className="flex items-center gap-1.5">
                                <span className="text-xs font-semibold text-[#6b7280]">Rate</span>
                                <input type="number" step="0.0001"
                                  value={leg.leg_fx_rate ?? ""}
                                  onChange={(e) => {
                                    const rate = parseFloat(e.target.value) || 0;
                                    setLeg(i, "leg_fx_rate", e.target.value);
                                    // rate = CAD→USD, so CAD equiv = USD / rate
                                    const legAmt = parseFloat(leg.amount) || 0;
                                    if (rate > 0 && legAmt > 0) {
                                      setLeg(i, "gift_cur_equivalent", (legAmt / rate).toFixed(2));
                                    }
                                  }}
                                  data-testid={`gift-leg-fx-rate-${i}`} placeholder="e.g., 0.7213"
                                  className="h-8 w-28 rounded border border-[#C2C9D1] px-2 text-sm" />
                              </label>
                              <span className="text-[#9aa1a9] text-xs font-medium">or</span>
                              <MoneyInput currency={legCurrency} size="sm" className="!w-36"
                                value={leg.amount ?? ""}
                                onChange={(v) => {
                                  const usdPaid = parseFloat(v) || 0;
                                  setLeg(i, "amount", v);
                                  // Calculate rate and gift-currency equivalent
                                  const giftEquiv = parseFloat(leg.gift_cur_equivalent) || legRemainingGiftCur || giftAmt;
                                  if (giftEquiv > 0 && usdPaid > 0) {
                                    const rate = usdPaid / giftEquiv;
                                    setLeg(i, "leg_fx_rate", String(rate.toFixed(7)));
                                    // Recalc equivalent from rate for consistency
                                    setLeg(i, "gift_cur_equivalent", (usdPaid / rate).toFixed(2));
                                  }
                                }}
                                testid={`gift-leg-usd-direct-${i}`} placeholder="e.g., 730.07" />
                            </div>
                          ) : (
                            <span className="text-[#6b7280]">
                              {fxRate > 0
                                ? `1 ${giftCur} = ${Number(fxRate).toFixed(5)} ${legCurrency} · from gift rate`
                                : <span className="italic text-[#F68F51]">Set gift date to auto-fetch rate</span>}
                            </span>
                          )}
                        </div>
                        {(() => {
                          const legAmt = parseFloat(leg.amount) || 0;
                          const legRate = leg.fx_override ? (parseFloat(leg.leg_fx_rate) || 0) : (fxRate || 0);
                          const equiv = leg.fx_override && leg.gift_cur_equivalent ? parseFloat(leg.gift_cur_equivalent) : (legRate > 0 && legAmt > 0 ? legAmt / legRate : 0);
                          return equiv > 0 ? <div className="mt-1.5 text-xs text-[#6b7280]">≈ {money(equiv, giftCur)}</div> : null;
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {legs.length > 0 && giftAmt > 0 && (() => {
              // Group actual amounts by currency for display
              const byCur = {};
              for (const l of legs) {
                const c = curCode(l.currency, giftCur);
                byCur[c] = (byCur[c] || 0) + (parseFloat(l.amount) || 0);
              }
              return (
                <div className="rounded-md bg-[#E9E9EB] px-2.5 py-1.5 text-xs" data-testid="gift-legs-balance">
                  <div className="flex items-center justify-between">
                    <span className="text-[#6b7280]">Transferred: {Object.entries(byCur).map(([c, t], idx) => <span key={c}>{idx > 0 && " + "}<b className="text-[#1f2937]">{money(t, c)}</b></span>)}
                      {Object.keys(byCur).some(c => !sameCurrency(c, giftCur)) && giftCurTransferred > 0 && (
                        <span className="ml-1 text-[#9aa1a9]">(≈ {money(giftCurTransferred, giftCur)} in {giftCur})</span>
                      )}
                    </span>
                    {remaining > 0.005 ? (
                      <span className="font-semibold text-[#D97706]">Balance: {money(remaining, giftCur)}</span>
                    ) : (
                      <span className="font-semibold text-[#077A39]">Fully transferred</span>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Document attachment */}
          <div className="col-span-2">
            <span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">Supporting documents (optional)</span>
            <DocumentsBox existing={storedDocs} onRemove={setRemoveDocId} files={docFiles} onFilesChange={setDocFiles} accept={RECEIPT_ACCEPT} testid="gift" />
          </div>

          <NotesField className="col-span-2" value={f.note} onChange={(v) => set("note", v)} testid="gift-note" />
        </div>
        <DialogFooter><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn enterPrimary variant="green" onClick={save} disabled={!f.student_id} data-testid="gift-save">Save</Btn></DialogFooter>
        <style>{`.inp{height:40px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 8px;font-size:14px;background:#fff}`}</style>
      </DialogContent>
      <Dialog open={!!removeDocId} onOpenChange={(o) => { if (!o) setRemoveDocId(null); }}>
        <DialogContent className="max-w-sm" data-testid="gift-doc-remove-dialog">
          <DialogHeader>
            <DialogTitle>Remove this document from the gift?</DialogTitle>
            <DialogDescription className="sr-only">Confirm removing this document from the gift</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-[#374151]">You can attach it again later. Nothing else about the gift changes.</p>
          <DialogFooter className="mt-2 gap-2">
            <button onClick={() => setRemoveDocId(null)} disabled={removing} data-testid="gift-doc-remove-cancel"
              className="flex-1 rounded-md border border-[#C2C9D1] bg-white px-4 py-2 text-sm font-bold text-[#374151] hover:bg-[#F4F7FB] disabled:opacity-40">Cancel</button>
            <button onClick={removeStoredDoc} disabled={removing} data-testid="gift-doc-remove-confirm"
              className="flex-1 rounded-md bg-[#F01919] px-4 py-2 text-sm font-bold text-white hover:bg-[#c81414] disabled:opacity-40">Remove</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
function L({ label, children, full }) { return <label className={`block ${full ? "col-span-2" : ""}`}><span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">{label}</span>{children}</label>; }
